import { Button } from '@/components/atoms';
import { Card } from '@/components/atoms/Card';
import { Typography } from '@/components/atoms/Typography';
// PhoneIcon, UserIcon: sales agent contact hidden for now
import { AppointmentConfirmedTickIcon, CalendarIcon, ClockIcon } from '@/components/atoms/icons';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
// import { openPhoneDialer } from '@/core/utils/phoneUtils'; // Sales agent contact hidden for now
import React, { useEffect, useRef } from 'react';
import { Modal, StyleSheet, View } from 'react-native'; // Pressable: sales agent contact hidden for now

export interface AppointmentConfirmedDialogProps {
    visible: boolean;
    /** Formatted date string, e.g. "5 August 2026" */
    date: string;
    /** Formatted time string, e.g. "12:30 PM" */
    time: string;
    // Sales agent contact hidden for now
    // /** Sales agent full name — omit/empty to hide the row (no mock fallback). */
    // agentName?: string | undefined;
    // /** Sales agent phone number — omit/empty to hide the row (no mock fallback). */
    // phoneNumber?: string | undefined;
    /** Custom button text; defaults to "Ok" */
    okText?: string;
    onOk: () => void;
}

export const AppointmentConfirmedDialog: React.FC<AppointmentConfirmedDialogProps> = ({
    visible,
    date,
    time,
    // Sales agent contact hidden for now
    // agentName,
    // phoneNumber,
    okText,
    onOk,
}) => {
    const colors = useUIColors();
    const { t } = useTranslation();
    const viewTrackedRef = useRef(false);
    useEffect(() => {
        if (!visible) {
            viewTrackedRef.current = false;
            return;
        }
        if (viewTrackedRef.current) {
            return;
        }
        viewTrackedRef.current = true;
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.APPOINTMENT,
            actionName: ALMETLAA_ACTION.APPOINTMENT.CONFIRMED_DIALOG_VIEW,
            eventType: 'view',
        });
    }, [visible]);
    /* Sales agent contact hidden for now
    const handleCallAgent = () => {
        if (phoneNumber) {
            openPhoneDialer(phoneNumber);
        }
    };
    */

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onOk}
        >
            <View style={styles.overlay}>
                <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
                    {/* Check Icon */}
                    <View style={styles.iconContainer}>
                        <AppointmentConfirmedTickIcon size={56} />
                    </View>

                    {/* Title */}
                    <Typography
                        variant="headline"
                        color="primary"
                        style={styles.title}
                    >
                        {t('propertyDetails.appointmentConfirmedTitle')}
                    </Typography>

                    {/* Details Card */}
                    <Card variant="default" padding={16} style={[styles.card, { backgroundColor: colors.backgroundLevel2 }]}>
                        {/* Date & Time Row */}
                        <View style={styles.dateTimeRow}>
                            {/* Date Column */}
                            <View style={styles.detailColumn}>
                                <Typography variant="caption" color="secondary">
                                    {t('propertyDetails.date')}
                                </Typography>
                                <View style={styles.detailValueRow}>
                                    <CalendarIcon size={16} color={colors.fontTertiary} />
                                    <Typography variant="body" color="primary" style={styles.detailValue}>
                                        {date}
                                    </Typography>
                                </View>
                            </View>

                            {/* Time Column */}
                            <View style={styles.detailColumnFill}>
                                <Typography variant="caption" color="secondary">
                                    {t('propertyDetails.time')}
                                </Typography>
                                <View style={styles.detailValueRow}>
                                    <ClockIcon size={16} color={colors.fontTertiary} />
                                    <Typography variant="body" color="primary" style={styles.detailValue}>
                                        {time}
                                    </Typography>
                                </View>
                            </View>
                        </View>


                        {/* Sales agent contact hidden for now
                        Agent Row — hidden entirely when neither value is available (no mock fallback)
                        {(agentName || phoneNumber) ? (
                            <View style={styles.dateTimeRow}>
                                // Contact Person Name Column
                                {agentName ? (
                                    <View style={styles.detailColumn}>
                                        <Typography variant="caption" color="secondary">
                                            {t('propertyDetails.contactPersonName')}
                                        </Typography>
                                        <View style={styles.detailValueRow}>
                                            <UserIcon size={16} color={colors.fontTertiary} />
                                            <Typography variant="body" color="primary" style={styles.detailValue}>
                                                {agentName}
                                            </Typography>
                                        </View>
                                    </View>
                                ) : null}

                                // Phone Number Column
                                {phoneNumber ? (
                                    <View style={styles.detailColumnFill}>
                                        <Typography variant="caption" color="secondary">
                                            {t('propertyDetails.phoneNumber')}
                                        </Typography>
                                        <Pressable
                                            style={styles.detailValueRow}
                                            onPress={handleCallAgent}
                                            hitSlop={8}
                                        >
                                            <PhoneIcon size={15} color={colors.fontTertiary} />
                                            <Typography variant="body" color="primary" style={styles.detailValue}>
                                                {phoneNumber}
                                            </Typography>
                                        </Pressable>
                                    </View>
                                ) : null}
                            </View>
                        ) : null}
                        */}
                    </Card>

                    {/* Ok Button */}
                    <Button
                        title={okText || t('common.ok')}
                        variant="primary"
                        onPress={onOk}
                        fullWidth
                        style={styles.button}
                        testID="appointment-confirmed-ok-button"
                    />
                </View>
            </View>
        </Modal>
    );
};


const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    container: {
        borderRadius: 16,
        paddingTop: 24,
        paddingBottom: 24,
        paddingHorizontal: 20,
        width: '100%',
        maxWidth: 340,
        alignItems: 'center',
        gap: 20,
    },
    iconContainer: {
        marginBottom: 0,
    },
    title: {
        textAlign: 'center',
        width: '100%',
    },
    card: {
        width: '100%',
        gap: 20,
    },
    dateTimeRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
    },
    detailColumn: {
        width: 130,
        gap: 8,
    },
    detailColumnFill: {
        flex: 1,
        gap: 8,
    },
    detailValueRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        flexShrink: 1,
    },
    detailValue: {
        fontSize: 14,
        lineHeight: 14,
        flexShrink: 1,
    },
    button: {
        width: '100%',
        height: 48,
    },
});

export default AppointmentConfirmedDialog;
